import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { PhaseType } from '../../Domain/enums/PhaseType';
import { Story } from '../../Domain/Story';
import { generateTimeDescriptiveId } from '../../Infrastructure/utilities';
import { WizardState, RootState } from '../types';
import { Artifact } from '../../Domain/artifacts/Artifact';
import { isPaidCustomer } from '../../Domain/SubscriptionUtilities';
import { resetAppState } from '../actions/resetAppState';

/**
 * Creates the initial state for the wizard slice
 * @returns Initial wizard state
 */
const createInitialState = (): WizardState => {
    // Initial state is always empty, demo data loaded later if needed
    return {
        story: {
            id: generateTimeDescriptiveId(),
            currentPhase: PhaseType.Ideas,
            event: { description: '' },
            goal: { description: '' },
            challenge: { description: '' },
            instructions: { description: '' },
            storyElements: []
        },
        artifact: undefined // Start with undefined artifact config
    };
};

const initialState: WizardState = createInitialState();

export const wizardSlice = createSlice({
    name: 'wizard',
    initialState,
    reducers: {
        setPhase: (state, action: PayloadAction<PhaseType>) => {
            state.story.currentPhase = action.payload;
        },
        setStoryElements: (state, action: PayloadAction<string[]>) => {
            state.story.storyElements = action.payload;
        },
        updateStory: (state, action: PayloadAction<Partial<Story>>) => {
            state.story = { ...state.story, ...action.payload };
        },
        updateArtifact: (state, action: PayloadAction<Partial<Artifact>>) => {
            // Ensure artifact exists before merging
            if (!state.artifact) {
                // If artifact doesn't exist, initialize it with the payload
                // This might happen if updateArtifact is called before loadDemoDataIfDemoPlan
                // We assume the payload contains enough base info (like type)
                state.artifact = action.payload as Artifact;
            } else {
                 // Merge partial updates into the existing artifact state
                state.artifact = { ...state.artifact, ...action.payload };
            }
        },
        clearStory: (state) => {
            state.story = {
                id: generateTimeDescriptiveId(),
                currentPhase: PhaseType.Ideas,
                event: { description: '' },
                goal: { description: '' },
                challenge: { description: '' },
                instructions: { description: '' },
                storyElements: []
            };
        }
    },
    extraReducers: (builder) => {
        builder.addCase(resetAppState, () => createInitialState());
    }
});

// Selector to check if user should be treated as demo/limited
export const selectIsDemoPlan = (state: RootState): boolean => {
    const subscription = state.subscription?.currentSubscription;
    return !isPaidCustomer(subscription);
};

export const {
    setPhase,
    setStoryElements,
    updateStory,
    updateArtifact,
    clearStory
} = wizardSlice.actions;

export default wizardSlice.reducer; 